<script lang="ts">
    /**
     * A select-like dropdown that can show an icon next to each option.
     * Uses a <details>/<summary> pattern (daisyUI dropdown) so images
     * can be placed inside the option list — unlike a native <select>.
     */
    export type IconOption = {
        value: string;
        label: string;
        icon?: string | null;
    };

    let {
        options,
        value = $bindable(),
        onchange,
        disabled = false,
        class: className = "",
    }: {
        options: IconOption[];
        value: string;
        onchange?: (value: string) => void;
        disabled?: boolean;
        class?: string;
    } = $props();

    const selected = $derived(options.find((o) => o.value === value) ?? options[0]);

    let detailsEl: HTMLDetailsElement = $state()!;
    let activeIndex = $state(-1);

    function select(opt: IconOption) {
        if (disabled) return;
        value = opt.value;
        activeIndex = -1;
        detailsEl.open = false;
        onchange?.(opt.value);
    }

    function handleOutsideClick(e: MouseEvent) {
        if (detailsEl?.open && !detailsEl.contains(e.target as Node)) {
            detailsEl.open = false;
            activeIndex = -1;
        }
    }

    function handleKeydown(e: KeyboardEvent) {
        if (!detailsEl?.open) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            activeIndex = (activeIndex + 1) % options.length;
            scrollActiveIntoView();
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            activeIndex = activeIndex <= 0 ? options.length - 1 : activeIndex - 1;
            scrollActiveIntoView();
        } else if (e.key === "Enter" && activeIndex >= 0) {
            e.preventDefault();
            select(options[activeIndex]);
        } else if (e.key === "Escape") {
            e.preventDefault();
            detailsEl.open = false;
            activeIndex = -1;
        }
    }

    function scrollActiveIntoView() {
        const listEl = detailsEl?.querySelector("ul");
        const activeEl = listEl?.children[activeIndex]?.querySelector("button");
        activeEl?.scrollIntoView({ block: "nearest" });
    }

    $effect(() => {
        document.addEventListener("click", handleOutsideClick, true);
        return () => document.removeEventListener("click", handleOutsideClick, true);
    });
</script>

<details bind:this={detailsEl} class="dropdown relative {className}">
    <summary
        class="flex items-center gap-1.5 rounded-full bg-base-300 px-4 py-1.5 h-9 text-sm font-semibold select-none transition-colors {disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer hover:bg-base-content/10'}"
        aria-haspopup="listbox"
        onclick={(e) => {
            if (disabled) e.preventDefault();
            else activeIndex = -1;
        }}
        onkeydown={handleKeydown}
    >
        {#if selected?.icon}
            <img src={selected.icon} alt="" width="24" height="16" class="shrink-0 rounded object-cover" />
        {/if}
        <span class="truncate">{selected?.label ?? ""}</span>
        <svg class="h-3 w-3 shrink-0 opacity-50" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"
            ><path d="M3 5l3 3 3-3" /></svg
        >
    </summary>
    <ul
        role="listbox"
        class="dropdown-content menu bg-base-200 rounded-box absolute top-full left-0 z-999 mt-1 max-h-80 w-full min-w-fit flex-nowrap overflow-y-auto p-1 shadow-lg text-sm"
    >
        {#each options as opt, i (opt.value)}
            <li>
                <button
                    role="option"
                    aria-selected={opt.value === value}
                    class="flex items-center gap-1.5 whitespace-nowrap text-sm {opt.value === value
                        ? 'active'
                        : ''} {i === activeIndex ? 'bg-base-content/10' : ''}"
                    onclick={() => select(opt)}
                >
                    {#if opt.icon}
                        <img src={opt.icon} alt="" width="28" height="18" class="shrink-0 rounded object-cover" />
                    {/if}
                    {opt.label}
                </button>
            </li>
        {/each}
    </ul>
</details>
